// dependencies
import { fromJS } from "immutable";
import { CreateProcess } from "redux-process";
import { publicApiHostName } from "../../Utils/getPublicApiHost";

// utils
import objectToQueryString from "Utils/objectToQueryString";

// selectors
import { getCurrentEnvironmentName } from "Selectors/app";

export const CompaniesSearch = CreateProcess({
	method: "GET",
	name: "CompaniesSearch",
	type: "COMPANIES:SEARCH",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		const queryString = objectToQueryString(props);

		return {
			url: `/${environment}/companies/?${queryString}`,
		};
	},

	success: (response) => fromJS(response.data),
});

export const GetCompany = CreateProcess({
	method: "GET",
	name: "GetCompany",
	type: "COMPANIES:FETCH.ONE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}`,
		};
	},

	success: (response) => fromJS(response.data.company),
	fail: (response) => fromJS(response.data.errors),
});

export const GetCompanySnapshot = CreateProcess({
	method: "GET",
	name: "GetCompanySnapshot",
	type: "Get company snapshot",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/snapshot`,
			payload: {
				__ignoreError: true,
			},
		};
	},

	success: (response) => {
		const { data } = response;

		// augment some date
		data.snapshot.averageReportCount = data.snapshot.averageReportCount.toFixed(1);
		data.snapshot.storageUsedRounded = `${data.snapshot.storageUsed.toFixed(1)} MB`;
		data.snapshot.storageAvailable = `${data.snapshot.storageAvailable.toFixed(1)} MB`;
		data.snapshot.averageAdminsPerGroup = data.snapshot.averageAdminsPerGroup.toFixed(1);
		data.snapshot.averageEmployeesPerGroup = data.snapshot.averageEmployeesPerGroup.toFixed(1);
		data.snapshot.averageGoalsPerActiveUser = data.snapshot.averageGoalsPerActiveUser.toFixed(1);
		data.snapshot.workflowsAverageTodosCount = data.snapshot.workflowsAverageTodosCount.toFixed(1);
		data.snapshot.goalsMedianProgress = `${(data.snapshot.goalsMedianProgress * 100).toFixed(1)}%`;
		data.snapshot.goalsAverageProgress = `${(data.snapshot.goalsAverageProgress * 100).toFixed(1)}%`;
		data.snapshot.averageStorageUsedPerUser = `${(data.snapshot.storageUsed / data.snapshot.activeSeats).toFixed(
			3
		)} MB`;
		data.snapshot.percentOfEmployeesWithAGoal = `${(
			(data.snapshot.activeUsersWithAGoal / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;
		data.snapshot.percentActiveUsersWithReview = `${(
			(data.snapshot.activeUsersReviewed / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;
		data.snapshot.percentActiveUsersWhoHaveGivenFeedback = `${(
			(data.snapshot.activeUsersThatHaveGivenFeedback / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;
		data.snapshot.percentActiveUsersWhoHaveReceivedFeedback = `${(
			(data.snapshot.activeUsersThatHaveReceivedFeedback / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;
		data.snapshot.percentActiveUsersWithAWorkflow = `${(
			(data.snapshot.activeUsersWithAWorkflow / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;

		data.snapshot.percentUsersParticipatingInAtLeastOneMeeting = `${(
			(data.snapshot.activeUsersInAtLeastOneMeeting / data.snapshot.activeSeats) *
			100
		).toFixed(1)}%`;
		return data;
	},
	fail: (response) => fromJS(response.data.errors),
});

export const UpdateCompanySnapshot = CreateProcess({
	method: "POST",
	name: "UpdateCompanySnapshot",
	type: "Update companies snapshot",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/update-snapshot/`,
		};
	},

	success: (response) => fromJS(response.data),
});

export const GetCompanyUsers = CreateProcess({
	method: "GET",
	name: "GetCompanyUsers",
	type: "COMPANIES:USERS.FETCHALL",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		const queryString = objectToQueryString(props.params);

		return {
			url: `/${environment}/companies/${props.companyID}/users/?${queryString}`,
		};
	},

	success: (response) => fromJS(response.data),
});

export const SetIntegration = CreateProcess({
	method: "POST",
	name: "SetIntegration",
	type: "COMPANIES:SETINTEGRATION",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/integration/`,
			payload: props.payload,
		};
	},

	success: (response) => fromJS(response.data),
});

export const DeleteIntegration = CreateProcess({
	method: "DELETE",
	name: "DeleteIntegration",
	type: "COMPANIES:INTEGRATION.DELETE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/integration/`,
			payload: props.payload,
		};
	},

	success: (response) => fromJS(response.data),
});

export const UpdateIntegration = CreateProcess({
	method: "PUT",
	name: "UpdateIntegration",
	type: "COMPANIES:INTEGRATION.UPDATE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/integration/`,
			payload: props.payload,
		};
	},

	success: (response) => fromJS(response.data),
});

export const UpdateCompanySetting = CreateProcess({
	method: "POST",
	name: "UpdateCompanySetting",
	type: "COMPANIES:SETTINGS.UPDATE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/update-setting`,
			payload: props.payload,
		};
	},

	success: (response) => fromJS(response.data),
});

export const UpdateCompanyTaxAddress = CreateProcess({
	method: "POST",
	name: "UpdateCompanyTaxAddress",
	type: "COMPANIES:TAX_ADDRESS.UPDATE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyId}/update-tax-address`,
			payload: props.payload,
		};
	},

	success: (response) => fromJS(response.data),
});

export const ExportEmployeeCsv = CreateProcess({
	method: "POST",
	name: "Export employee csv",
	type: "Export employee csv",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/export/employees`,
		};
	},

	success: (response) => response.data,
});

export const ResetNotificationDefaults = CreateProcess({
	method: "POST",
	name: "reset notification defaults",
	type: "COMPANIES:RESET.NOTIFICATIONS",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/reset-notification-defaults`,
		};
	},

	success: (response) => response.data,
});

export const ExportGoalCsv = CreateProcess({
	method: "POST",
	name: "Export goal csv",
	type: "Export goal csv",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/export/goals`,
		};
	},

	success: (response) => response.data,
});

export const copyStagingData = CreateProcess({
	method: "POST",
	name: "copy staging data",
	type: "COMPANIES:COPY.STAGE",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/copy-staging-data`,
			payload: props.payload,
		};
	},

	success: (response) => response.data,
});

export const RunReportingETL = CreateProcess({
	method: "POST",
	name: "run reporting etl",
	type: "COMPANIES:RUN.ETL",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);
		const { companyID, reportingDomain } = props;

		return {
			url: `/${environment}/companies/${companyID}/reporting-etl/${reportingDomain}`,
		};
	},

	success: (response) => response.data,
});

export const SendSurveyManually = CreateProcess({
	method: "POST",
	name: "sendSurveyManually",
	type: "COMPANIES:SEND.SURVEY.MANUALLY",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);
		const { companyID, env } = props;

		return {
			url: `/${environment}/companies/${companyID}/send-survey-manually`,
			payload: {
				sendSurveyEnv: env,
			},
		};
	},

	success: (response) => response.data,
});

export const SendReminderEmailsManually = CreateProcess({
	method: "POST",
	name: "sendReminderEmailsManually",
	type: "COMPANIES:SEND.REMINDER_EMAILS.MANUALLY",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);
		const { companyID, env } = props;

		return {
			url: `/${environment}/companies/${companyID}/send-reminder-emails-manually`,
			payload: {
				sendReminderEmailsEnv: env,
			},
		};
	},

	success: (response) => response.data,
});

export const GetCompanyInvoice = CreateProcess({
	method: "GET",
	name: "GetCompanyInvoice",
	type: "Get company Invoice",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/invoice`,
			payload: {
				__ignoreError: true,
			},
		};
	},
	success: (response) => {
		fromJS(response.data.invoice);
		return response.data.invoice;
	},
	fail: (response) => fromJS(response.data.errors),
});

export const UpdateCompanyInvoice = CreateProcess({
	method: "POST",
	name: "UpdateCompanyInvoice",
	type: "Update company Invoice",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/update-invoice`,
			payload: props.payload,
		};
	},
	success: (response) => fromJS(response.data),
	fail: (response) => fromJS(response.data.errors),
});

export const CancelCompanyInvoice = CreateProcess({
	method: "POST",
	name: "CancelCompanyInvoice",
	type: "Cancel company Invoice",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		return {
			url: `/${environment}/companies/${props.companyID}/cancel-invoice`,
			payload: {
				__ignoreError: true,
			},
		};
	},
	success: (response) => fromJS(response.data),
	fail: (response) => fromJS(response.data.errors),
});

/**
 * Fetch a company's recently deleted objects.
 */
export const RecentlyDeletedObjects = CreateProcess({
	method: "GET",
	name: "RecentlyDeletedObjects",
	type: "Deleted Objects",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		const { companyID, ...query } = props.params;

		const queryString = objectToQueryString(query);

		return {
			url: `/${environment}/companies/${companyID}/deleted-objects/?${queryString}`,
		};
	},

	success: (response) => response.data,
});

/**
 * Call the Lambda function to undelete a review report
 */
export const UndeleteReviewReport = CreateProcess({
	method: "POST",
	name: "UndeleteReviewReport",
	type: "Deleted Objects",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		const { companyID, reviewReportID } = props.params;
		const payload = { companyID, reviewReportID };

		return {
			url: `/${environment}/companies/${companyID}/undelete-review-report/`,
			payload,
		};
	},

	success: (response) => fromJS(response.data),
	fail: (response) => fromJS(response.data.errors),
});

/**
 * Send request to undelete the review
 */
export const UndeleteReview = CreateProcess({
	method: "POST",
	name: "UndeleteReview",
	type: "Deleted Objects",

	request: (props, { getState }) => {
		const state = getState();
		const environment = getCurrentEnvironmentName(state);

		const { companyId, reviewId } = props.params;
		const payload = { reviewId };

		return {
			url: `/${environment}/companies/${companyId}/undelete-review`,
			payload,
		};
	},

	success: (response) => fromJS(response.data),
	fail: (response) => fromJS(response.data.errors),
});
